import { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import "./PdfAtestadoModelo2.scss";
import moment from "moment/moment";
import "moment/locale/pt";

moment.locale("pt");

const numeroExtenso = (num) => {
  const numeros = {
    0: "zero",
    1: "um",
    2: "Dois",
    3: "três",
    4: "quatro",
    5: "cinco",
    6: "seis",
    7: "sete",
    8: "oito",
    9: "nove",
    10: "dez",
    11: "onze",
    12: "doze",
    13: "treze",
    14: "catorze",
    15: "quinze",
    16: "dezasseis",
    17: "dezassete",
    18: "dezoito",
    19: "dezanove",
    20: "vinte",
  };
  return numeros[Number(num)] || num;
};

function PdfDeclaracao({ atestadoData, showPreviousPage }) {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Declaracao-${atestadoData.cliente.nome}_${moment(
      new Date(),
    ).format("DD-MM-YYYY")}`,
    onAfterPrint: () => {
      showPreviousPage();
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    handlePrint();
  }, [handlePrint]);

  const isPretoebranco =
    atestadoData.clinica?.configuracoes?.configuracoes?.impressaoPretoEBranco
      ?.ativo;

  // Format the date parts separately for the dd/mm/yyyy blanks
  const fonteGeorgia = atestadoData.clinica?._id === "6817794da65ef747fc74730d";

  const dataAtestado = atestadoData.data_atestado
    ? moment(atestadoData.data_atestado)
    : null;

  const dia = dataAtestado ? dataAtestado.format("DD") : "____";
  const mes = dataAtestado ? dataAtestado.format("MM") : "____";
  const ano = dataAtestado ? dataAtestado.format("YYYY") : "________";

  const horaInicial = atestadoData.hora_inicial
    ? moment(atestadoData.hora_inicial).format("HH:mm")
    : "__________";

  const horaFinal = atestadoData.hora_final
    ? moment(atestadoData.hora_final).format("HH:mm")
    : "__________";

  const diasRepouso = atestadoData.dias ?? "__________";
  const diasRepousoExtenso = numeroExtenso(atestadoData.dias) ?? "__________";

  const obs = atestadoData.observacao ?? "";

  // Date line at bottom: "Praia, ___ de ___________ de 20___"
  const cidadeData = atestadoData.clinica?.ilha ?? "Praia";
  const diaAtual = moment().format("D");
  const mesAtual = moment().format("MMMM");
  const anoAtual = moment().format("YY");

  return (
    <div
      ref={componentRef}
      className={`pdf-declaracao-wrapper${isPretoebranco ? " pdf-wrapper--pretoebranco" : ""}${fonteGeorgia ? " pdf-declaracao--georgia" : ""}`}
    >
      <div className="pdf-declaracao-body">
        {/* HEADER — centered logo */}
        <div className="pdf-declaracao__cabecalho">
          <img
            src={`${process.env.REACT_APP_BACKEND_LINK}/${atestadoData.clinica.logotipo}`}
            alt={atestadoData.clinica.nome}
            className="pdf-declaracao__cabecalho__logo"
          />
        </div>

        {/* TITLE */}
        <h2 className="pdf-declaracao__title">DECLARAÇÃO</h2>

        {/* BODY TEXT */}
        <div className="pdf-declaracao__corpo">
          <p className="pdf-declaracao__texto">
            Atesto que o(a) Sr.(a){" "}
            <span style={{ fontStyle: "italic", fontWeight: "700" }}>
              {atestadoData.cliente.nome || ""}
            </span>{" "}
            esteve sob tratamento na {atestadoData.clinica?.nome} em consulta
            médica, no dia{" "}
            {dia ? (
              <span>{dia}</span>
            ) : (
              <>
                /
                <span className="pdf-declaracao__linha pdf-declaracao__linha--curta">
                  {mes}
                </span>
                /
                <span className="pdf-declaracao__linha pdf-declaracao__linha--media">
                  {ano}
                </span>
              </>
            )}
            , no período de{" "}
            {horaInicial ? (
              <span>{horaInicial}</span>
            ) : (
              <span className="pdf-declaracao__linha pdf-declaracao__linha--media"></span>
            )}
            às{" "}
            {horaFinal ? (
              <span>{horaFinal}</span>
            ) : (
              <span className="pdf-declaracao__linha pdf-declaracao__linha--media"></span>
            )}
            , necessitando o mesmo de{" "}
            {diasRepouso ? (
              <span>{diasRepouso}</span>
            ) : (
              <span className="pdf-declaracao__linha pdf-declaracao__linha--media"></span>
            )}
            (<span className="">{diasRepousoExtenso}</span>) dia(s) de repouso.
          </p>

          {/* OBS */}
          <div className="pdf-declaracao__obs">
            <span className="pdf-declaracao__obs__label">Obs:</span>
            <span className="pdf-declaracao__obs__linha">{obs}</span>
          </div>

          {/* DATE LINE */}
          <div className="pdf-declaracao__data">
            {cidadeData}, <span>{diaAtual}</span> de {mesAtual} de 20
            <span>{anoAtual}</span>
          </div>

          {/* SIGNATURE */}
          <div className="pdf-declaracao__assinatura">
            <span className="pdf-declaracao__assinatura__titulo">
              Médico Assistente
            </span>

            {atestadoData.medico?.assinatura?.ativa &&
            atestadoData.medico?.assinatura?.autorizacoes?.atestados &&
            atestadoData.medico?.assinatura?.imagem ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_LINK}/${atestadoData.medico.assinatura.imagem}`}
                alt="Assinatura"
                className="pdf-declaracao__assinatura__img"
              />
            ) : (
              <div className="pdf-declaracao__assinatura__linha" />
            )}

            <span className="pdf-declaracao__assinatura__carimbo">
              Carimbo e assinatura
            </span>
          </div>
        </div>

        {/* FOOTER */}
        <div className="pdf-declaracao__rodape">
          <span>
            {atestadoData.clinica.nome}
            {atestadoData.clinica.endereco}
          </span>
          <span>
            {[
              atestadoData.clinica.contacto,
              atestadoData.clinica.contacto2,
              atestadoData.clinica.contacto3,
            ]
              .filter(Boolean)
              .join(" / ")}
            {atestadoData.clinica.email
              ? ` - E-mail: ${atestadoData.clinica.email}`
              : ""}
            {atestadoData.clinica.nif
              ? ` - NIF: ${atestadoData.clinica.nif}`
              : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

export default PdfDeclaracao;
